/* eslint-disable camelcase */

import { Action, PayloadAction } from '@reduxjs/toolkit';
import { propEq } from 'ramda';
import { ofType, combineEpics, StateObservable } from 'redux-observable';
import {
  Observable, interval, EMPTY, zip,
  fromEvent,
} from 'rxjs';
import {
  tap,
  ignoreElements,
  timestamp,
  map,
  switchMap,
  pluck,
  filter,
  throttle,
  skip,
  distinctUntilChanged,
  first,
  takeUntil,
} from 'rxjs/operators';

import { sendAnalytics } from '@ezugi/analytics/sendAnalytics';
import { BonusWheelStartGEMessage } from '@ezugi/bootstrap/src/views/GameBonus/types';
import {
  DISCONNECTED_FROM_OPERATOR,
  mq,
  GE,
  BonusMessage,
} from '@ezugi/constants';
import { GenericPayloadType } from '@ezugi/types';
import {
  DeviceInfo,
  getDiffTimeAsADouble,
  getFrame,
  NetworkPerformanceTracker,
} from '@ezugi/utils';
import { ORIENTATION } from '@ezugi/utils/src/helpers/getOrientation';

import { getMinMaxLimits } from '../../../util/getMinMaxLimits';
import {
  authActions,
  betActions,
  configActions,
  dialogActions,
  errorActions,
  gameActions,
  lobbyActions,
  localeActions,
  mediaActions,
  menuActions,
  notificationActions,
  performanceActions,
  playerHistoryActions,
  preloaderActions,
  socketActions,
  tipsActions,
  videoActions,
} from '../../actions';
import {
  ERROR_SHOW,
  VIDEO_ERROR,
  NO_BALANCE,
  DISCONNECT_SHOW,
  AUTO_ADJUST,
  GAME_LOADING,
  GAME_LOADED,
  HOME_CLICK,
  PLAYER_DETAILED_HISTORY_CLICK,
  CASHIER_CLICK,
  CHANGE_LANGUAGE,
  HIDE_OTHERS_CHAT,
  GAME_SOUNDS,
  VIDEO_STREAM_SOUNDS,
  GAME_VOLUME,
  OPEN_MINI_LOBBY,
  OPEN_MINI_LOBBY_TABLE,
  TOGGLE_TIPS,
  SEND_TIP,
  BET_ACTION_REBET,
  BET_ACTION_DOUBLE,
  BET_ACTION_UNDO,
  BET_ACTION_CLEAR,
  SELECT_CHIP,
  LAST_BET_ACTION,
  CHANGE_DEFAULT_CHIP,
  BET_ACTION_REQUEST,
  BACKGROUND_MUSIC,
  REFERRER_URLS_TO_IGNORE,
  DIRECT_LINK,
  TAP_TO_PLAY,
  TAP_TO_PLAY_VISIBLE,
  VIDEO_EVO,
  VIDEO_NANO,
  PERFORMANCE_LOAD_TABLE_CONFIG_LOADING_STATUS_MAP,
  VIDEO_STATUS_EVENT,
  DIALOG_SHOW,
  MINI_LOBBY_CATEGORY_CLICK,
  PERFORMANCE_GET_USER_LOADING_STATUS_MAP,
  LOBBY_FAVORITE,
  PERFORMANCE_ENGINE_LOADING_STATUS_MAP,
  CHAT_LOADING,
  CHANGE_ORIENTATION,
  GAME_MENU_PLAYER_HISTORY_DAILY,
  BONUS_WHEEL_SHOW,
} from '../../constants/analytics';
import {
  LOW_BALANCE_DIALOG,
  DISCONNECTED_FROM_OPERATOR_DIALOG,
} from '../../constants/dialog';
import {
  toggleMute as chatToggleMute,
  setCredentials,
  gettingCredentialsFailed,
  ChatCredentials,
} from '../../reducers/chat';
import { configActionsFromSlice } from '../../reducers/config';
import {
  tableIdSelector,
  roundStatusSelector,
  userSelector,
  currentLanguageSelector,
  gameSoundsSelector,
  volumeSelector,
  validatedChipsSelector,
  selectedChipSelector,
  minBetSelector,
  showTipsSelector,
  roundSelector,
  limitIdSelector,
  totalBetSelector,
  gameIdSelector,
  backgroundMusicSoundSelector,
  videoStreamSoundSelector,
  roundIdSelector,
  videoDelaySelector,
  isEvoPlayerSelector,
  userBalanceSelector,
  currencySelector,
  currentLimitsSelector,
  tableSelector,
  isTapToPlayVisibleSelector,
  widgetsSelector,
} from '../../selectors';
import {
  engineRequestTimestampSelector,
  loadTableConfigRequestTimestampSelector,
  initialDataSelector,
  getChatUserRequestTimestampSelector,
  videoRequestTimestampSelector,
} from '../../selectors/analytics';
import { isChatMutedSelector } from '../../selectors/chat';
import { getInitialChip } from '../auth/utils';

import type { RootState } from '../../types';

const { video } = videoActions;
const { dialog } = dialogActions;
const { notification } = notificationActions;
const { home, cashier, menu } = menuActions;
const { roundDetails } = playerHistoryActions;
const { locale } = localeActions;
const { sound, music, volume, videoStreamSound } = mediaActions;
const { lobby } = lobbyActions;
const { tips } = tipsActions;
const { bet, history } = betActions;
const { selectChip } = gameActions;
const { preloader } = preloaderActions;
const {
  loadTableConfigPerformance,
  enginePerformance,
  getChatUserPerformance,
  setGameLoaded,
  videoPerformance,
} = performanceActions;
const { auth } = authActions;
const { socket } = socketActions;
const { playerHistory } = playerHistoryActions;

const config = process.env.NODE_ENV !== 'test'
  ? configActions.config : (configActions.config || configActionsFromSlice);

function getGeneralInfo(data: Record<string, any>) {
  return {
    tableId: Number(data.table_id),
    operatorId: data.operator_id,
    playerId: data.player_id,
    gameTypeId: data.gameTypeId,
    language_code: data.language_code,
    currency: data.currency,
    game_name: data.game_name,
    limit_id: data.limit_id,
    device: data.device,
    browser: data.browser,
    frame: data.frame,
    platform: data.platform,
    resolution: data.resolution,
    full_screen: data.full_screen,
    min_limit: data.min_limit,
    max_limit: data.max_limit,
    clientId: data.clientId,
  };
}

export async function injectAnalytics() {
  // Add GTM
  import('@ezugi/analytics');
}

const getTotalTime = (stateValue: RootState) => {
  // TODO: REFACTOR THIS!!!! https://ezugi1.atlassian.net/browse/ERD-15755
  const el = document.querySelector('[data-current-seconds]');
  const { totalTime: realTotalTime } = roundSelector(stateValue);
  const actionTime: number = el
    ? parseFloat(el.getAttribute('data-current-seconds') || '0')
    : 0;
  const videoDelay = videoDelaySelector(stateValue);
  const totalTime: number = (realTotalTime ?? 0) + videoDelay;

  return { actionTime, realTotalTime, totalTime };
};

const pushEventInAnalytics = (stateValue: RootState, event: string) => {
  const { actionTime, realTotalTime, totalTime } = getTotalTime(stateValue);

  return sendAnalytics({
    event,
    roundId: roundIdSelector(stateValue),
    actionTime,
    totalTime,
    realTotalTime,
  });
};

const directLinkEpic = (action$: Observable<Action>) => action$.pipe(
  filter(config.request.match),
  filter(
    () => !REFERRER_URLS_TO_IGNORE.find((url) => document.referrer.includes(url))
  ),
  tap(() => {
    sendAnalytics({
      event: DIRECT_LINK,
      referrer: document.referrer,
    });
  }),
  ignoreElements()
);

const fatalErrorEpic = (action$: Observable<Action>) => action$.pipe(
  filter(errorActions.error.set.match),
  pluck('payload'),
  tap(({ code, message, error, label, title, description }: any) => sendAnalytics({
    event: ERROR_SHOW,
    error_id: code,
    error_name: message || error || label || title || description,
  })),
  ignoreElements()
);

const videoErrorEpic = (action$: Observable<any>) => action$.pipe(
  filter(
    (action) => video.failure.type === action.type
        || video.error.type === action.type
        || video.disconnect.type === action.type
        || video.analyticsError.type === action.type
  ),
  pluck('payload'),
  filter(
    (payload) => payload && payload.reason !== 'normal' && payload.code !== 1007
  ),
  tap(({ code, message, error, label, title, description, videoPlayer }) => {
    if (videoPlayer === VIDEO_EVO) {
      // error.description contains error code in string e.g. "Request failed with status code: 0"
      const statusCodeMatch = error.description?.match(/\b\d+\b/);
      code = statusCodeMatch ? parseInt(statusCodeMatch[0], 10) : 0;
      message = error.reason || error.description;
    }

    sendAnalytics({
      event: VIDEO_ERROR,
      error_id: code,
      error_name: message || error || label || title || description,
    });
  }),
  ignoreElements()
);

const videoAnalyticsInitEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(
    (action) => video.analyticsInit.type === action.type
        || video.analyticsSuccess.type === action.type
  ),
  // stop consecutive Ready actions.
  distinctUntilChanged(
    (prevAction, currAction) => prevAction.type === currAction.type
        && currAction.type === videoActions.video.analyticsSuccess.type
  ),
  map(({ payload, type }) => {
    const isSuccess = type === videoActions.video.analyticsSuccess.type;
    const eventTimestamp = Date.now();
    const generalInfo = {
      ...getGeneralInfo({
        ...initialDataSelector(state$.value),
        ...userSelector(state$.value),
      }),
    };
    const requestTimestamp = videoRequestTimestampSelector(state$.value);

    sendAnalytics({
      event: VIDEO_STATUS_EVENT,
      videoStatus: payload.videoStatus,
      tableId: generalInfo.tableId,
      gameId:
          generalInfo.gameTypeId
          || payload.session?.gameType
          || gameIdSelector(state$.value),
      name: generalInfo.game_name,
      operatorId: generalInfo.operatorId,
      videoPlayer: payload.videoPlayer,
      resolution: generalInfo.resolution,
      platform: generalInfo.platform,
      frame: generalInfo.frame,
      loadingTime: isSuccess && requestTimestamp
        ? getDiffTimeAsADouble(eventTimestamp, requestTimestamp)
        : 0,
      // currentQuality is on EvoPlayer, frameRate is on NanoPlayer
      quality: payload.logic?.currentQuality || payload.videoInfo?.frameRate,
    });

    return isSuccess
      ? videoPerformance.setResponseTimestamp(eventTimestamp)
      : videoPerformance.setRequestTimestamp(eventTimestamp);
  })
);

const dialogShowEpic = (
  action$: Observable<any>,
  _state$: StateObservable<RootState>
) => action$.pipe(
  filter(dialog.add.match),
  pluck('payload'),
  tap((payload) => sendAnalytics({
    dialog_name: payload.name,
    dialog_attributes: {
      ...payload,
    },
    event: DIALOG_SHOW,
  })),
  ignoreElements()
);

const lowBalanceEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(dialog.add.match),
  map((action: any) => action.payload),
  filter(propEq('name', LOW_BALANCE_DIALOG)),
  tap(() => {
    const limitsState = currentLimitsSelector(state$.value);
    const gameId = gameIdSelector(state$.value);
    const table = tableSelector(state$.value);

    const { min: limit_min, max: limit_max } = getMinMaxLimits({
      gameId: gameId!,
      limits: limitsState,
    });

    const limit_id = limitsState.id;

    // Note: limit id from currentLimitsSelector is number, but limit id from tableSelector is string
    const limit_index = (Object.keys(table.limits) ?? []).findIndex(
      // eslint-disable-next-line eqeqeq
      (l) => +l === limit_id
    );

    sendAnalytics({
      event: NO_BALANCE,
      error_name: NO_BALANCE,
      stage: roundStatusSelector(state$.value),
      balance: userBalanceSelector(state$.value),
      currency: currencySelector(state$.value),
      limit_id,
      limit_index,
      limit_min,
      limit_max,
    });
  }),
  ignoreElements()
);

const disconnectionFromOperatorEpic = (action$: Observable<any>) => action$.pipe(
  filter(dialog.add.match),
  map((action: any) => action.payload),
  filter(propEq('name', DISCONNECTED_FROM_OPERATOR_DIALOG)),
  tap(() => sendAnalytics({
    event: DISCONNECT_SHOW,
    disconnect_type: DISCONNECTED_FROM_OPERATOR,
  })),
  ignoreElements()
);

const autoAdjustEpic = (action$: Observable<any>) => action$.pipe(
  filter(notification.add.match),
  map((action: any) => action.payload),
  filter((payload) => Boolean(payload?.autoAdjust)),
  tap(({ event_name }: { event_name: string }) => sendAnalytics({
    event: AUTO_ADJUST,
    message: event_name,
  })),
  ignoreElements()
);

/**
 * Send event when clicking the menu home button
 * and redirecting successfully
 */
const homeClickEventEpic = (action$: Observable<any>) => action$.pipe(
  filter(home.request.match),
  throttle(() => interval(1000)),
  tap(() => sendAnalytics({
    event: HOME_CLICK,
  })),
  ignoreElements()
);

/**
 * Send event when successfully opening the detailed player history
 */
const detailedHistoryEpic = (action$: Observable<any>) => action$.pipe(
  filter(roundDetails.success.match),
  tap(() => sendAnalytics({
    event: PLAYER_DETAILED_HISTORY_CLICK,
  })),
  ignoreElements()
);

const cashierClickEpic = (action$: Observable<any>) => action$.pipe(
  filter(cashier.request.match),
  pluck('payload'),
  tap(({ source }: any) => sendAnalytics({
    event: CASHIER_CLICK,
    source,
  })),
  ignoreElements()
);

const localeChangeEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(locale.change.match),
  pluck('payload'),
  tap(({ code }) => {
    const previous = currentLanguageSelector(state$.value).code;

    sendAnalytics({
      event: CHANGE_LANGUAGE,
      previous,
      current: code,
    });
  }),
  ignoreElements()
);

const hideOthersChatToggleEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((payload) => payload.type === chatToggleMute.type),
  tap(() => sendAnalytics({
    event: HIDE_OTHERS_CHAT,
    value: isChatMutedSelector(state$.value as any),
  })),
  ignoreElements()
);

const gameSoundEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(sound.toggle.match),
  tap(() => sendAnalytics({
    event: GAME_SOUNDS,
    roundId: roundIdSelector(state$.value),
    value: gameSoundsSelector(state$.value),
  })),
  ignoreElements()
);

const videoStreamSoundsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(videoStreamSound.toggle.match),
  tap(() => sendAnalytics({
    event: VIDEO_STREAM_SOUNDS,
    roundId: roundIdSelector(state$.value),
    value: videoStreamSoundSelector(state$.value),
  })),
  ignoreElements()
);

const backgroundMusicEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(music.toggle.match),
  tap(() => sendAnalytics({
    event: BACKGROUND_MUSIC,
    roundId: roundIdSelector(state$.value),
    value: backgroundMusicSoundSelector(state$.value),
  })),
  ignoreElements()
);

const gameVolumeEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(volume.set.match),
  // Skipping the first `volume.set` because of the AutoUnmute on click
  skip(1),
  throttle(() => interval(1000)),
  tap(() => sendAnalytics({
    event: GAME_VOLUME,
    roundId: roundIdSelector(state$.value),
    value: volumeSelector(state$.value).value,
  })),
  ignoreElements()
);

const openMiniLobbyEpic = (action$: Observable<any>) => action$.pipe(
  filter(lobby.toggle.match),
  timestamp(),
  switchMap(({ timestamp: start }) => action$.pipe(
    ofType(lobby.open),
    timestamp(),
    map(({ timestamp: end }) => ({ start, end }))
  )),
  tap(({ start, end }) => sendAnalytics({
    event: OPEN_MINI_LOBBY,
    duration: getDiffTimeAsADouble(end, start),
  })),
  ignoreElements()
);

const openMiniLobbyFromMenuEpic = (action$: Observable<any>) => action$.pipe(
  filter(lobby.openMenuWidget.match),
  timestamp(),
  switchMap(({ timestamp: start }) => action$.pipe(
    filter(lobby.patch.match),
    first(),
    timestamp(),
    map(({ timestamp: end }) => ({ start, end }))
  )),
  tap(({ start, end }) => sendAnalytics({
    event: OPEN_MINI_LOBBY,
    duration: getDiffTimeAsADouble(end, start),
    fromMenu: true,
  })),
  ignoreElements()
);

const openMiniLobbyTableEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(lobby.navigate.match),
  pluck('payload'),
  tap((payload) => sendAnalytics({
    event: OPEN_MINI_LOBBY_TABLE,
    previous: tableIdSelector(state$.value),
    current: +payload.table.id,
  })),
  ignoreElements()
);

const desktopToggleTipsEpic = (action$: Observable<any>) => action$.pipe(
  filter(menu.set.match),
  pluck('payload'),
  filter(({ widget }: any) => widget.key && widget.key === 'tips'),
  pluck('widget', 'drawer'),
  distinctUntilChanged(),
  tap((open) => sendAnalytics({
    event: TOGGLE_TIPS,
    open,
  })),
  ignoreElements()
);

const mobileToggleTipsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(
    (action) => tips.toggleSuccess.match(action) || tips.reset.match(action)
  ),
  tap(() => sendAnalytics({
    event: TOGGLE_TIPS,
    open: showTipsSelector(state$.value),
  })),
  ignoreElements()
);

const toggleTipsEpic = DeviceInfo.isHandheld
  ? mobileToggleTipsEpic
  : desktopToggleTipsEpic;

const sendTipEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(tips.send.match),
  pluck('payload', 'tipValue'),
  switchMap((tipValue) => action$.pipe(
    filter(tips.success.match),
    tap(() => sendAnalytics({
      event: SEND_TIP,
      value: tipValue,
      roundId: roundIdSelector(state$.value),
    })),
    ignoreElements()
  )),
  ignoreElements()
);

const betActionRebetEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(bet.rebet.match),
  tap(() => pushEventInAnalytics(state$.value, BET_ACTION_REBET)),
  ignoreElements()
);

const betActionDoubleEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(bet.double.match),
  tap(() => pushEventInAnalytics(state$.value, BET_ACTION_DOUBLE)),
  ignoreElements()
);

const betActionUndoEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(history.pop.match),
  tap(() => pushEventInAnalytics(state$.value, BET_ACTION_UNDO)),
  ignoreElements()
);

const betActionClearEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(bet.clear.match),
  tap(() => pushEventInAnalytics(state$.value, BET_ACTION_CLEAR)),
  ignoreElements()
);

function onChipChanged(event: string, state: RootState) {
  const currentChip = selectedChipSelector(state);
  const defaultChip = getInitialChip({
    chips: validatedChipsSelector(state),
    minBet: minBetSelector(state),
  });

  sendAnalytics({
    event,
    default: defaultChip.value,
    current: currentChip ? currentChip.value : 0,
    limitId: limitIdSelector(state),
  });
}

const defaultChipChangedEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(selectChip.set.match),
  filter(({ payload }) => {
    const defaultChip = getInitialChip({
      chips: validatedChipsSelector(state$.value),
      minBet: minBetSelector(state$.value),
    });

    return payload.value !== defaultChip.value;
  }),
  first(),
  tap(() => onChipChanged(CHANGE_DEFAULT_CHIP, state$.value)),
  ignoreElements()
);

const selectChipEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(selectChip.set.match),
  skip(1),
  tap(() => onChipChanged(SELECT_CHIP, state$.value)),
  ignoreElements()
);

const lastBetActionEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(
    (action) => bet.set.type === action.type
        || bet.rebet.type === action.type
        || bet.double.type === action.type
        || history.pop.type === action.type
        || bet.clear.type === action.type
        || bet.update.type === action.type
  ),
  switchMap(() => {
    const { actionTime, realTotalTime, totalTime } = getTotalTime(
      state$.value
    );

    if (actionTime == null) return EMPTY;

    return action$.pipe(
      filter(socket.send.match),
      filter(({ payload }) => payload.MessageType === GE.OUT.SOCKET_SEND.MESSAGE_TYPE.PLACE_BET),
      tap(() => {
        sendAnalytics({
          event: LAST_BET_ACTION,
          totalTime,
          realTotalTime,
          actionTime,
          roundId: roundIdSelector(state$.value),
          totalBet: totalBetSelector(state$.value),
          video_player: isEvoPlayerSelector(state$.value)
            ? VIDEO_EVO
            : VIDEO_NANO,
        });
      }),
      ignoreElements()
    );
  }),
  ignoreElements()
);

const betActionRequestEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(bet.set.match),
  tap(() => sendAnalytics({
    event: BET_ACTION_REQUEST,
    roundId: roundIdSelector(state$.value),
  })),
  ignoreElements()
);

const preloaderTapEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(preloader.tap.match),
  filter(() => isTapToPlayVisibleSelector(state$.value)),
  tap(() => sendAnalytics({
    event: TAP_TO_PLAY,
    ...getGeneralInfo({
      ...initialDataSelector(state$.value),
      ...userSelector(state$.value),
    }),
  })),
  ignoreElements()
);

const preloaderShowEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(preloader.show.match),
  filter(() => DeviceInfo.isHandheld),
  filter(() => isTapToPlayVisibleSelector(state$.value)),
  tap(() => sendAnalytics({
    event: TAP_TO_PLAY_VISIBLE,
    ...getGeneralInfo({
      ...initialDataSelector(state$.value),
      ...userSelector(state$.value),
    }),
  })),
  ignoreElements()
);

const loadTableConfigRequestEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(
    (action) => config.request.type === action.type
        || config.failure.type === action.type
        || config.success.type === action.type
  ),
  takeUntil(action$.pipe(ofType(setGameLoaded))),
  map(({ type, payload }) => {
    const isInitialReq = type === config.request.type;
    const isFailedReq = type === config.failure.type;
    const eventTimestamp = Date.now();
    const requestTimestamp = loadTableConfigRequestTimestampSelector(
      state$.value
    );

    sendAnalytics({
      event: GAME_LOADING,
      loadingStatus: (
          PERFORMANCE_LOAD_TABLE_CONFIG_LOADING_STATUS_MAP as Record<
            string,
            string
          >
      )[type],
      loadingTime: isInitialReq || !requestTimestamp
        ? 0
        : getDiffTimeAsADouble(eventTimestamp, requestTimestamp),
      ...(payload?.code && isFailedReq && { errorInfo: payload.code }),
    });

    return isInitialReq
      ? loadTableConfigPerformance.setRequestTimestamp(eventTimestamp)
      : loadTableConfigPerformance.setResponseTimestamp(eventTimestamp);
  })
);

const engineRequestEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  filter((action) => socket.request.type === action.type
   || socket.success.type === action.type
   || socket.failure.type === action.type),
  takeUntil(action$.pipe(ofType(setGameLoaded))),
  map(({ type, payload }) => {
    const isInitialReq = type === socketActions.socket.request.type;
    const isFailedReq = type === socketActions.socket.failure.type;
    const eventTimestamp = Date.now();
    const requestTimestamp = engineRequestTimestampSelector(state$.value);

    sendAnalytics({
      event: GAME_LOADING,
      loadingStatus: (PERFORMANCE_ENGINE_LOADING_STATUS_MAP as Record<string, string>)[type],
      loadingTime: isInitialReq || !requestTimestamp
        ? 0
        : getDiffTimeAsADouble(eventTimestamp, requestTimestamp),
      ...(isFailedReq
          && payload?.error?.message && { errorInfo: payload.error.message }),
    });

    return isInitialReq
      ? enginePerformance.setRequestTimestamp(eventTimestamp)
      : enginePerformance.setResponseTimestamp(eventTimestamp);
  })
);

const getChatUserRequestEpic = (
  action$: Observable<
    PayloadAction<ChatCredentials | GenericPayloadType | undefined>
  >,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((action) => preloader.hide.match(action)
  || gettingCredentialsFailed.match(action)
  || setCredentials.match(action)),
  filter(() => Boolean(widgetsSelector(state$.value)?.chat)),
  map(({ type, payload }) => {
    const isInitialReq = type === preloader.hide.type;
    const isFailedReq = type === gettingCredentialsFailed.type;
    const eventTimestamp = Date.now();
    const requestTimestamp = getChatUserRequestTimestampSelector(
      state$.value
    );

    sendAnalytics({
      event: CHAT_LOADING,
      loadingStatus: (PERFORMANCE_GET_USER_LOADING_STATUS_MAP as Record<string, string>)[type],
      loadingTime: isInitialReq || !requestTimestamp
        ? 0
        : getDiffTimeAsADouble(eventTimestamp, requestTimestamp),
      ...(isFailedReq
          && (payload as any)?.error && {
        errorInfo:
              (payload as any).error.code || (payload as any).error.message,
      }),
    });

    return isInitialReq
      ? getChatUserPerformance.setRequestTimestamp(eventTimestamp)
      : getChatUserPerformance.setResponseTimestamp(eventTimestamp);
  })
);

const gameFullyLoadedEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(config.success.match),
  takeUntil(action$.pipe(ofType(setGameLoaded))),
  switchMap(() => zip(
    action$.pipe(ofType(auth.success)),
    action$.pipe(ofType(video.analyticsSuccess)),
    action$.pipe(ofType(socket.success)),
    action$.pipe(ofType(video.success)),
    action$.pipe(ofType(preloader.hide))
  ).pipe(
    map(() => {
      const loadingTime = getDiffTimeAsADouble(
        Date.now(),
        loadTableConfigRequestTimestampSelector(state$.value) as number
      );

      NetworkPerformanceTracker.getInstance().stopTracking();

      sendAnalytics({
        event: GAME_LOADED,
        loadingTime,
        pageURL: location.href,
      });

      return setGameLoaded(loadingTime);
    })
  ))
);

const lobbyCategoryClickEpic = (action$: Observable<any>) => action$.pipe(
  filter(lobby.categoryClick.match),
  map(({ payload }) => payload),
  tap((payload: any) => sendAnalytics({
    event: MINI_LOBBY_CATEGORY_CLICK,
    ...payload,
  })),
  ignoreElements()
);

const lobbyFavoriteEpic = (action$: Observable<Action>) => action$.pipe(
  filter(lobby.toggleFavorite.match),
  map(({ payload }) => payload),
  tap((payload) => {
    sendAnalytics({
      event: LOBBY_FAVORITE,
      status: !payload.favorite,
    });
  }),
  ignoreElements()
);

const orientationChangeMobileEpic = () => fromEvent<MediaQueryListEvent>(window.matchMedia(mq.portrait), 'change').pipe(
  filter(() => DeviceInfo.isHandheld),
  tap((event) => {
    const sharedProperties = {
      event: CHANGE_ORIENTATION,
      frame: getFrame(),
    };

    if (event.matches) {
      sendAnalytics({
        ...sharedProperties,
        oldpos: ORIENTATION.landscape,
        newpos: ORIENTATION.portrait,
      });
    } else {
      sendAnalytics({
        ...sharedProperties,
        oldpos: ORIENTATION.portrait,
        newpos: ORIENTATION.landscape,
      });
    }
  }),
  ignoreElements()
);

const playerHistoryRequestRoundEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(playerHistory.requestRounds.match),
  tap(() => {
    sendAnalytics({
      event: GAME_MENU_PLAYER_HISTORY_DAILY,
      roundId: roundIdSelector(state$.value),
      origin: 'game',
    });
  }),
  ignoreElements()
);

const bonusWheelShowEpic = (action$: Observable<PayloadAction<BonusWheelStartGEMessage>>) => action$.pipe(
  filter(socket.message.match),
  map(({ payload }) => payload),
  filter((payload) => payload.MessageType === BonusMessage.BonusWheelStart),
  tap(() => {
    sendAnalytics({
      event: BONUS_WHEEL_SHOW,
    });
  }),
  ignoreElements()
);

export default combineEpics(
  directLinkEpic,
  fatalErrorEpic,
  videoErrorEpic,
  dialogShowEpic,
  lowBalanceEpic,
  disconnectionFromOperatorEpic,
  autoAdjustEpic,
  homeClickEventEpic,
  detailedHistoryEpic,
  cashierClickEpic,
  localeChangeEpic,
  hideOthersChatToggleEpic,
  gameSoundEpic,
  backgroundMusicEpic,
  gameVolumeEpic,
  videoStreamSoundsEpic,
  openMiniLobbyEpic,
  openMiniLobbyTableEpic,
  toggleTipsEpic,
  sendTipEpic,
  betActionRebetEpic,
  betActionDoubleEpic,
  betActionUndoEpic,
  betActionClearEpic,
  selectChipEpic,
  defaultChipChangedEpic,
  lastBetActionEpic,
  betActionRequestEpic,
  preloaderTapEpic,
  preloaderShowEpic,
  loadTableConfigRequestEpic,
  engineRequestEpic,
  videoAnalyticsInitEpic,
  getChatUserRequestEpic,
  gameFullyLoadedEpic,
  openMiniLobbyFromMenuEpic,
  lobbyCategoryClickEpic,
  lobbyFavoriteEpic,
  orientationChangeMobileEpic,
  playerHistoryRequestRoundEpic,
  bonusWheelShowEpic
);
